import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Table,
  DataList,
  Spinner,
  Badge,
  Grid,
} from '@radix-ui/themes';
import {
  IdCardIcon,
  ArrowLeftIcon,
  BellIcon,
  BarChartIcon,
  ActivityLogIcon,
} from '@radix-ui/react-icons';
import { AddressDisplay } from '../../components/AddressDisplay';
import DetailPageLayout from '../../components/DetailPageLayout';
import { useGetEntityQuery } from '../../graphql/queries/GetEntity.generated';

// Helper to calculate risk level from warnings
const getRiskLevel = (
  warningCount: number,
): { level: string; color: string; percentage: number } => {
  if (warningCount === 0)
    return { level: 'Low', color: 'green', percentage: 15 };
  if (warningCount <= 2)
    return { level: 'Medium', color: 'yellow', percentage: 45 };
  return { level: 'High', color: 'red', percentage: 75 };
};

const EntityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading, error } = useGetEntityQuery({
    variables: { id },
    skip: !id,
  });

  const entity = data?.entity;
  const addresses = entity?.addresses.edges.map((edge) => edge.node) ?? [];
  const riskCount = entity?.risks.totalCount ?? 0;
  const riskLevel = getRiskLevel(riskCount);

  // Mock data - These would come from backend in future
  const mockDescription = 'Technology research and development company';
  const mockTags = ['Technology', 'R&D', 'Verified'];
  const mockTotalAssetValue = '$9,948,497.60';
  const mockCreatedDate = '06/12/2025';
  const mockUpdatedDate = '12/12/2025';
  const mockTotalTransactions = '1,247';
  const mockLastActivity = '2 hours ago';
  const mockPrimaryChain = 'Ethereum';

  if (loading) {
    return (
      <Flex align="center" justify="center" py="9">
        <Spinner size="3" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex align="center" justify="center" py="9">
        <Text color="red">Error loading entity: {error.message}</Text>
      </Flex>
    );
  }

  if (!entity) {
    return (
      <Flex align="center" justify="center" py="9">
        <Text color="gray">Entity not found</Text>
      </Flex>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Flex align="center" gap="3" mb="4" px="6" py="4">
        <Button variant="ghost" onClick={() => navigate('/entities')}>
          <ArrowLeftIcon width={16} height={16} />
          Back to Entities
        </Button>
      </Flex>

      <Flex direction="column" gap="5" px="6" pb="6">
        {/* Title and Actions */}
        <Flex align="start" justify="between">
          <Flex align="start" gap="4">
            <Box
              style={{
                width: 60,
                height: 60,
                borderRadius: 'var(--radius-3)',
                backgroundColor: 'var(--purple-3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IdCardIcon width={28} height={28} color="var(--purple-11)" />
            </Box>
            <Flex direction="column" gap="2">
              <Flex align="center" gap="3">
                <Heading size="6">{entity.name}</Heading>
                <Badge
                  color={
                    riskLevel.color as 'red' | 'yellow' | 'green' | undefined
                  }
                  size="2"
                >
                  {riskLevel.level.toUpperCase()} RISK
                </Badge>
              </Flex>
              <Text size="2" color="gray">
                {mockDescription}
              </Text>
              <Flex gap="2" mt="1">
                {mockTags.map((tag) => (
                  <Badge key={tag} variant="soft" size="1">
                    {tag}
                  </Badge>
                ))}
              </Flex>
            </Flex>
          </Flex>
          <Flex gap="3">
            <Button variant="outline">
              <BellIcon />
              Create Alert
            </Button>
            <Button variant="outline">
              <BarChartIcon />
              View Analytics
            </Button>
            <Button>
              <Box>Add Address</Box>
            </Button>
          </Flex>
        </Flex>

        {/* Three Column Layout */}
        <Grid columns="3" gap="4" width="100%">
          {/* Left Column - Activity Summary */}
          <Card size="2">
            <Heading as="h3" size="4" mb="4">
              ACTIVITY SUMMARY
            </Heading>
            <Flex direction="column" gap="4">
              <Flex align="center" gap="3">
                <Box
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-2)',
                    backgroundColor: 'var(--blue-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BarChartIcon width={20} height={20} color="var(--blue-11)" />
                </Box>
                <Box>
                  <Text size="2" color="gray" mb="1" as="div">
                    Total Transactions
                  </Text>
                  <Heading size="5">{mockTotalTransactions}</Heading>
                </Box>
              </Flex>

              <Flex align="center" gap="3">
                <Box
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-2)',
                    backgroundColor: 'var(--purple-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ActivityLogIcon
                    width={20}
                    height={20}
                    color="var(--purple-11)"
                  />
                </Box>
                <Box>
                  <Text size="2" color="gray" mb="1" as="div">
                    Last Activity
                  </Text>
                  <Text size="3" weight="medium">
                    {mockLastActivity}
                  </Text>
                </Box>
              </Flex>

              <Flex align="center" gap="3">
                <Box
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-2)',
                    backgroundColor: 'var(--indigo-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'var(--indigo-11)',
                    }}
                  />
                </Box>
                <Box>
                  <Text size="2" color="gray" mb="1" as="div">
                    Primary Chain
                  </Text>
                  <Text size="3" weight="medium">
                    {mockPrimaryChain}
                  </Text>
                </Box>
              </Flex>

              <Flex align="center" gap="3">
                <Box
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-2)',
                    backgroundColor: 'var(--teal-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ActivityLogIcon
                    width={20}
                    height={20}
                    color="var(--teal-11)"
                  />
                </Box>
                <Box>
                  <Text size="2" color="gray" mb="1" as="div">
                    First Seen
                  </Text>
                  <Text size="3" weight="medium">
                    {entity.createdAt
                      ? new Date(entity.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Card>

          {/* Middle Column - Entity Information */}
          <Card size="2">
            <Heading as="h3" size="4" mb="4">
              Entity Information
            </Heading>
            <Flex direction="column" gap="4">
              <Box>
                <Text size="2" weight="medium" color="gray" mb="1" as="div">
                  ASSOCIATED ADDRESSES
                </Text>
                <Text size="5" weight="bold">
                  {addresses.length}
                </Text>
              </Box>
              <Box>
                <Text size="2" weight="medium" color="gray" mb="1" as="div">
                  TOTAL ASSET VALUE
                </Text>
                <Text size="5" weight="bold" style={{ color: 'var(--teal-11)' }}>
                  {mockTotalAssetValue}
                </Text>
              </Box>
            </Flex>
          </Card>

          {/* Right Column - Risk Assessment */}
          <Card size="2">
            <Heading as="h3" size="4" mb="4">
              RISK ASSESSMENT
            </Heading>
            <Flex direction="column" gap="4">
              <Flex align="center" gap="3">
                <Box
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 'var(--radius-2)',
                    backgroundColor: `var(--${riskLevel.color}-3)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text size="3" weight="bold">
                    ⚠️
                  </Text>
                </Box>
                <Box>
                  <Heading size="5" mb="1">
                    {riskLevel.level}
                  </Heading>
                  <Text size="2" color="gray">
                    {riskCount} {riskCount === 1 ? 'Risk' : 'Risks'} Identified
                  </Text>
                </Box>
              </Flex>

              {entity.risks.edges.length > 0 && (
                <Flex direction="column" gap="2">
                  {entity.risks.edges.slice(0, 3).map((edge, idx) => (
                    <Box
                      key={idx}
                      style={{
                        padding: 'var(--space-2)',
                        backgroundColor: 'var(--gray-2)',
                        borderRadius: 'var(--radius-2)',
                        borderLeft: `3px solid var(--${riskLevel.color}-9)`,
                      }}
                    >
                      <Text size="2" weight="medium">
                        {edge.node.description}
                      </Text>
                    </Box>
                  ))}
                  {entity.risks.totalCount > 3 && (
                    <Text size="2" color="gray" style={{ textAlign: 'center' }}>
                      +{entity.risks.totalCount - 3} more risks
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          </Card>
        </Grid>

        {/* Associated Addresses Section */}
        <Card size="2">
          <Flex justify="between" align="center" mb="4">
            <Heading as="h3" size="4">
              Associated Addresses ({addresses.length})
            </Heading>
            <Text size="2" color="gray">
              Blockchain addresses linked to this entity
            </Text>
          </Flex>
          {addresses.length === 0 ? (
            <Flex align="center" justify="center" py="6">
              <Text color="gray">No addresses associated with this entity.</Text>
            </Flex>
          ) : (
            <Box
              style={{
                marginLeft: 'calc(-1 * var(--space-4))',
                marginRight: 'calc(-1 * var(--space-4))',
                marginBottom: 'calc(-1 * var(--space-4))',
              }}
            >
              <Table.Root size="2">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>
                      <Flex align="center" gap="2">
                        <Badge variant="soft" color="blue" size="1">
                          Ethereum
                        </Badge>
                        <Badge variant="soft" size="1">
                          Risk Score: 45
                        </Badge>
                      </Flex>
                    </Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Balance</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Transactions</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Last Activity</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Added</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {addresses.map((address, index) => (
                    <Table.Row
                      key={`${address.address}-${index}`}
                      style={
                        index === addresses.length - 1
                          ? { borderBottom: 'none' }
                          : {}
                      }
                    >
                      <Table.Cell>
                        <AddressDisplay address={address} showEntityName={false} />
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" weight="medium" style={{ color: 'var(--teal-11)' }}>
                          $9,948,497.6
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">1,247</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          2 hours ago
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2" color="gray">
                          {address.createdAt
                            ? new Date(address.createdAt).toLocaleDateString()
                            : 'N/A'}
                        </Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card>
      </Flex>
    </Box>
  );
};

export default EntityDetail;
